import { useState } from "react";
import api from "../lib/api";
import SEO from "../components/SEO";
import { ArrowRight, Check } from "lucide-react";

const PROFILES = ["Amoureux de culture", "Famille", "Pro / presse", "Curieux", "Mélomane"];

export default function Festival() {
    const [form, setForm] = useState({ email: "", full_name: "", city: "", profile: "" });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.email.includes("@")) {
            setError("Email invalide.");
            return;
        }
        setStatus("loading");
        try {
            await api.post("/festival/waitlist", form);
            setStatus("success");
        } catch (e) {
            setStatus("idle");
            setError("Oups, réessayez dans un instant.");
        }
    };

    return (
        <>
            <SEO
            title="Festival Côte Perdue — Été 2026"
            description="Le festival Côte Perdue, Été 2026 : musique, gastronomie et patrimoine entre Le Tréport, Mers-les-Bains et Cayeux-sur-Mer. Programmation et billetterie en préparation."
            path="/festival"
        />
            <div data-testid="festival-page">
            <section className="relative min-h-[90vh] overflow-hidden cp-grain">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/img/festival_dinner.jpg`} alt="Festival Côte Perdue" className="absolute inset-0 w-full h-full object-cover cp-kenburns cp-photo-dark" />
                <div className="absolute inset-0 bg-cp-encre/65" />
                <div className="relative z-10 min-h-[90vh] flex flex-col justify-end px-6 md:px-12 max-w-[1600px] mx-auto py-28">
                    <p className="cp-tiny text-cp-sable mb-8">Chapitre VI · Un projet</p>
                    <h1 className="cp-serif text-cp-ivoire text-[44px] sm:text-[64px] md:text-[88px] lg:text-[112px] xl:text-[140px] leading-[0.94] tracking-[-0.025em]">
                        Festival<br /><em className="cp-display-italic text-cp-sable">Côte Perdue.</em>
                    </h1>
                    <p className="cp-serif italic text-cp-ivoire/85 text-xl md:text-2xl font-light leading-snug mt-12 max-w-3xl">
                        Un événement éditorial en préparation, pensé avec le territoire. Trois nuits quelque part entre Mers et Aumale. Voix rares, tables très longues, jardins clos, levers de soleil. <em>Les dates et le lieu seront communiqués dès qu'ils seront verrouillés.</em>
                    </p>
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-16">
                <div className="md:col-span-6">
                    <p className="cp-kicker text-cp-foret mb-6">L'intention</p>
                    <h2 className="cp-serif text-4xl md:text-5xl leading-tight text-cp-noir mb-8">
                        Un week-end pensé comme un dîner entre connaisseurs.
                    </h2>
                    <ul className="flex flex-col gap-4 text-cp-noir text-lg font-light">
                        <li className="flex gap-3"><span className="text-cp-foret">—</span> Une programmation éclectique, choisie à l'oreille</li>
                        <li className="flex gap-3"><span className="text-cp-foret">—</span> Des tables longues dans des lieux du territoire</li>
                        <li className="flex gap-3"><span className="text-cp-foret">—</span> Artisans d'art, parfumeurs, libraires en invités</li>
                        <li className="flex gap-3"><span className="text-cp-foret">—</span> Projections, lectures, conférences tardives</li>
                        <li className="flex gap-3"><span className="text-cp-foret">—</span> Hébergements à quelques pas des scènes</li>
                        <li className="flex gap-3"><span className="text-cp-foret">—</span> Une jauge tenue — tout le monde ne viendra pas</li>
                    </ul>
                </div>

                <div className="md:col-span-5 md:col-start-8">
                    <div className="border border-cp-border p-8 md:p-12 bg-cp-craie">
                        <p className="cp-kicker text-cp-foret mb-4">Être prévenu</p>
                        <h3 className="cp-serif text-3xl md:text-4xl leading-tight text-cp-noir mb-6">
                            Recevoir l'annonce avant tout le monde.
                        </h3>
                        {status === "success" ? (
                            <div data-testid="festival-success" className="flex flex-col gap-3 pt-4">
                                <p className="flex items-center gap-2 text-cp-foret text-lg"><Check size={18} strokeWidth={1.5} /> Vous êtes sur la liste.</p>
                                <p className="text-cp-gris text-sm font-light">Dès que la date, le lieu et la programmation seront arrêtés, vous serez prévenu avant tous les autres.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="festival-form" className="flex flex-col gap-5 pt-2">
                                <input type="text" placeholder="Prénom Nom" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="cp-input" data-testid="festival-name" />
                                <input type="email" required placeholder="votre@adresse.email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cp-input" data-testid="festival-email" />
                                <input type="text" placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="cp-input" />
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {PROFILES.map((p) => (
                                        <button type="button" key={p} onClick={() => setForm({ ...form, profile: p })}
                                            className={`cp-kicker text-[10px] px-3 py-2 border ${form.profile === p ? "border-cp-foret bg-cp-foret text-cp-ivoire" : "border-cp-border"}`}>
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <button type="submit" disabled={status === "loading"} className="cp-btn-primary mt-4" data-testid="festival-submit">
                                    Rejoindre la liste <ArrowRight size={14} strokeWidth={1.5} />
                                </button>
                                {error && <p className="text-red-600 text-sm">{error}</p>}
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    </>

    );
}
